import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { WsClient } from "./ws-client";

/**
 * R-202 — app-wide live WsClient context. The web app previously never
 * instantiated a `WsClient`, so every `useQueues` / `useQueueCounts` caller
 * passed `null` and the WS subscriptions (R-182, R-162 §10.1) were inert.
 * `WsProvider` instantiates and connects ONE client and exposes it via
 * `useWsClient()`. Mount it INSIDE the auth gate (only connects post-login,
 * when the `capture_session` cookie is present) and ABOVE the consumers
 * (AppShell `useQueueCounts`, QueueDashboard `useQueues`).
 */
const WsContext = createContext<WsClient | null>(null);

export function useWsClient(): WsClient | null {
  return useContext(WsContext);
}

export function WsProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<WsClient | null>(null);
  const ref = useRef<WsClient | null>(null);

  useEffect(() => {
    // Ref guard: React 18 StrictMode double-mounts effects in dev. Without
    // this guard we'd open (and leak) two sockets on the first mount.
    if (ref.current) return;

    // The server authenticates the WS upgrade from the same-origin HttpOnly
    // `capture_session` cookie (websocket-auth.ts::resolveWsSessionId). The
    // auth-message `sessionId` is a deprecated fallback, and JS cannot read
    // the HttpOnly cookie — so a sentinel ("") is the correct arg here.
    const c = new WsClient("");
    ref.current = c;
    c.connect();
    setClient(c);

    return () => {
      ref.current?.disconnect();
      ref.current = null;
      setClient(null);
    };
  }, []);

  return <WsContext.Provider value={client}>{children}</WsContext.Provider>;
}
